import { useEffect, useState } from "react";
import { useForm } from "../../components/forms/useForm";
import { connect } from "react-redux";
import * as actions from "../../store/actions/index";
import { useParams, useNavigate } from "react-router-dom";

import { DocumentIcon } from "@heroicons/react/24/outline";
import ImportMediaDropzone from "../../components/importMedia/ImportMediaDropzone";
import { EquipmentMaintenanceViewModel } from "../../viewmodels/equipmentMaintenance/EquipmentMaintenanceViewModel";
import EquipmentViewModel from "../../viewmodels/setup/equipmentViewModel/EquipmentViewModel";

import Input from "../../components/controls/Input";
import SelectMenu from "../../components/controls/SelectMenu";
import DatePickerInput from "../../components/controls/DatePickerInput";
import TextArea from "../../components/controls/TextArea";
import AuditActionLink from "../../components/audit/AuditActionLink";
import { convertToDefEventPara, withRouter } from "../../shared/utility";
import { equipmentMaintenanceStatusOptions } from "../../shared/utility";

const initialFValues = {
  equipment_id: null,
  equipment_status: null,
  requested_by: "",
  maintenance_date: "",
  completed_date: "",
  verified_by: "",
  notes: "",
  document1: "",
  document2: "",
};

function EquipmentMaintenanceForm(props) {
  const { logout, addSnackbar } = props;
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [document1Preview, setDocument1Preview] = useState(null);
  const [document2Preview, setDocument2Preview] = useState(null);
  const { values, setValues, errors, setErrors, handleInputChange } = useForm(
    initialFValues,
    false
  );

  const { fetchEquipmentsForDropdown, equipmentOptions } = EquipmentViewModel(
    logout,
    0,
    addSnackbar,
  );

  const { createMaintenance, updateMaintenance, getMaintenanceItemByID } =
    EquipmentMaintenanceViewModel(logout, addSnackbar);

  useEffect(() => {
    fetchEquipmentsForDropdown();

    // Load data if in edit mode
    if (isEdit) {
      getMaintenanceItemByID(id).then((data) => {
        if (data) {
          // Convert equipment_status to option object format for SelectMenu
          const formattedData = {
            ...data,
            equipment_status: data.equipment_status
              ? equipmentMaintenanceStatusOptions.find(
                  (o) => o.label === data.equipment_status
                ) || null
              : null,
          };
          setValues(formattedData);
        }
      });
    }
  }, [id, isEdit]);

  const validate = (fieldValues = values) => {
    let temp = { ...errors };

    if ("equipment_id" in fieldValues) {
      temp.equipment_id = null;
      if (!fieldValues.equipment_id) {
        temp.equipment_id = "Equipment is required.";
      }
    }

    if ("requested_by" in fieldValues) {
      temp.requested_by = null;
      if (!fieldValues.requested_by) {
        temp.requested_by = "Requested by is required.";
      }
    }

    if ("maintenance_date" in fieldValues) {
      temp.maintenance_date = null;
      if (!fieldValues.maintenance_date) {
        temp.maintenance_date = "Maintenance date is required.";
      }
    }

    setErrors({
      ...temp,
    });

    if (fieldValues === values) {
      return Object.values(temp).every((x) => x === null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const payload = { ...values };

      if (payload.equipment_id && typeof payload.equipment_id === "object") {
        payload.equipment_id = payload.equipment_id.value;
      }
      if (
        payload.equipment_status &&
        typeof payload.equipment_status === "object"
      ) {
        payload.equipment_status = payload.equipment_status.label;
      }
      if (payload.document1 === null) {
        payload.document1 = "null";
      }

      if (payload.document2 === null) {
        payload.document2 = "null";
      }

      if (isEdit) {
        updateMaintenance(id, payload);
      } else {
        createMaintenance(payload);
      }
      navigate("/equipment-maintenance");
    }
  };

  return (
    <div>
      <div className="mt-2 px-4 sm:px-8">
        <div className="sm:flex sm:items-center justify-between gap-3 px-4 py-5 sm:px-4 card-custom">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold leading-6 text-gray-900">
              {isEdit ? "Edit" : "Create"} Equipment Maintenance Record
            </h1>
          </div>
          {isEdit && values.equipment_id ? (
            <AuditActionLink
              slug="equipment"
              id={values.equipment_id}
              name={
                equipmentOptions.find((o) => o.value === values.equipment_id)
                  ?.label
              }
            />
          ) : null}
        </div>

        <form className="mt-6 card-custom p-4" onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4">
            <SelectMenu
              handleInputChange={handleInputChange}
              name="equipment_id"
              options={equipmentOptions}
              value={
                equipmentOptions.find((o) => o.value === values.equipment_id) ||
                null
              }
              error={errors.equipment_id}
              title="Equipment"
            />

            <SelectMenu
              handleInputChange={handleInputChange}
              name="equipment_status"
              options={equipmentMaintenanceStatusOptions}
              value={values.equipment_status}
              error={errors.equipment_status}
              title="Equipment Status"
            />

            <Input
              label="Requested By"
              name="requested_by"
              onChange={handleInputChange}
              value={values.requested_by}
              error={errors.requested_by}
              labelOn={true}
              type="text"
            />

            <Input
              label="Verified By"
              name="verified_by"
              onChange={handleInputChange}
              value={values.verified_by}
              error={errors.verified_by}
              labelOn={true}
              type="text"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <DatePickerInput
              label="Maintenance Date"
              name="maintenance_date"
              onChange={handleInputChange}
              value={values.maintenance_date}
              error={errors.maintenance_date}
              labelOn={true}
              type="text"
              dateFormat={"LLL dd, YYY"}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />

            <DatePickerInput
              label="Completed Date"
              name="completed_date"
              onChange={handleInputChange}
              value={values.completed_date}
              error={errors.completed_date}
              labelOn={true}
              type="text"
              dateFormat={"LLL dd, YYY"}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />

            <div className="col-span-2">
              <TextArea
                label="Notes"
                name="notes"
                onChange={handleInputChange}
                value={values.notes}
                error={errors.notes}
                labelOn={true}
                rows={4}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              />
            </div>

            {/* Document Upload Section */}
            <div className="col-span-2 mt-6 border-t pt-6">
              <h3 className="text-sm font-medium text-gray-900 mb-4">
                Maintenance Documents
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Document 1 */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Document 1
                  </label>
                  {document1Preview ? (
                    <div className="mb-2">
                      <DocumentIcon className="h-32 w-32 text-gray-400" />
                    </div>
                  ) : values.document1 &&
                    typeof values.document1 === "string" &&
                    values.document1.startsWith("http") ? (
                    <div className="mb-2">
                      <DocumentIcon className="h-32 w-32 text-gray-400" />
                      <a
                        href={values.document1}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-blue-600 hover:underline mt-2 block"
                      >
                        View document
                      </a>
                    </div>
                  ) : null}
                  <div className="flex gap-2">
                    <ImportMediaDropzone
                      fileName="document1"
                      handleInputChange={handleInputChange}
                      setUploadedImage={setDocument1Preview}
                    />
                    {(values.document1 || document1Preview) && (
                      <button
                        type="button"
                        className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
                        onClick={() => {
                          setValues({ ...values, document1: null });
                          setDocument1Preview(null);
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>

                {/* Document 2 */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Document 2
                  </label>
                  {document2Preview ? (
                    <div className="mb-2">
                      <DocumentIcon className="h-32 w-32 text-gray-400" />
                    </div>
                  ) : values.document2 &&
                    typeof values.document2 === "string" &&
                    values.document2.startsWith("http") ? (
                    <div className="mb-2">
                      <DocumentIcon className="h-32 w-32 text-gray-400" />
                      <a
                        href={values.document2}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-blue-600 hover:underline mt-2 block"
                      >
                        View document
                      </a>
                    </div>
                  ) : null}
                  <div className="flex gap-2">
                    <ImportMediaDropzone
                      fileName="document2"
                      handleInputChange={handleInputChange}
                      setUploadedImage={setDocument2Preview}
                    />
                    {(values.document2 || document2Preview) && (
                      <button
                        type="button"
                        className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
                        onClick={() => {
                          setValues({ ...values, document2: null });
                          setDocument2Preview(null);
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 mr-3"
              onClick={() => window.history.back()}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="ml-4 inline-flex justify-center rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white"
            >
              {isEdit ? "Save" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
    addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(EquipmentMaintenanceForm)
);
